/**
 * The Spotify header (spec `docs/superpowers/specs/2026-10-17-music24-spotify-header-design.md`,
 * design `spotify-header/a`): the 2660 x 1140 picture at the top of the Spotify artist page. A
 * Studio image of the song (asset type `spotifyHeader`), made by code with no director.
 *
 * This file is the one source of the picture choice (the params), the zones, the crop and the
 * drawn grounds. music24-api renders with it (sharp), the web Studio preview draws with it, and the
 * native apps copy the zone values. No imports outside fym-shared.
 */
import type { ThemeColours } from './releaseThemes';

/** The size of the file (`ASSET_SPECS.spotifyHeader`). */
export const SPOTIFY_HEADER_SIZE = { width: 2660, height: 1140 } as const;

/**
 * Where the picture comes from (spec §2.2). `cover`: the song cover, blurred, with a wash of the
 * theme ground. `photo`: an image file of the account (the profile photo or an upload). `bank`: a
 * published cover bank picture. `ai`: a ready AI picture of the song from the cover editor.
 * `theme`: a calm field in the theme colours.
 */
export const SPOTIFY_HEADER_SOURCES = ['cover', 'photo', 'bank', 'ai', 'theme'] as const;
export type SpotifyHeaderSource = (typeof SPOTIFY_HEADER_SOURCES)[number];

export const SPOTIFY_HEADER_DEFAULT_SOURCE: SpotifyHeaderSource = 'cover';

export function isSpotifyHeaderSource(value: unknown): value is SpotifyHeaderSource {
  return typeof value === 'string' && (SPOTIFY_HEADER_SOURCES as readonly string[]).includes(value);
}

/** The id that a source needs: a file id (`photo`), a picture uuid (`bank`, `ai`), or none. */
export function spotifyHeaderSourceNeeds(source: SpotifyHeaderSource): 'file' | 'picture' | null {
  if (source === 'photo') {
    return 'file';
  }
  if (source === 'bank' || source === 'ai') {
    return 'picture';
  }
  return null;
}

/** True when the artist can move the picture (`focusX`, `focusY`). */
export function spotifyHeaderSourceMoves(source: SpotifyHeaderSource): boolean {
  return spotifyHeaderSourceNeeds(source) !== null;
}

/** A box in shares of the file (0 to 1). */
export interface HeaderBox {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/**
 * The zones of the file (spec §2.4). Spotify publishes no crop: the phone, desktop and name values
 * are our measure (GUESS), from third-party guides and a community report. Change them here only.
 */
export const SPOTIFY_HEADER_ZONES: Readonly<{
  /** The part that a phone shows (GUESS): the middle half, the full height. */
  phone: HeaderBox;
  /** The part that a wide desktop window shows (GUESS): the full width, the middle two thirds. */
  desktop: HeaderBox;
  /** Where Spotify draws the round photo and the name (GUESS): bottom left. */
  name: HeaderBox;
  /** The box that every crop shows, clear of the name: keep faces here. */
  subject: HeaderBox;
}> = {
  phone: { x0: 0.25, x1: 0.75, y0: 0, y1: 1 },
  desktop: { x0: 0, x1: 1, y0: 1 / 6, y1: 5 / 6 },
  name: { x0: 0, x1: 0.48, y0: 0.62, y1: 1 },
  subject: { x0: 0.3, x1: 0.75, y0: 0.17, y1: 0.64 },
};

/** The default point of the picture that goes to the middle of the subject box. */
export const SPOTIFY_HEADER_DEFAULT_FOCUS = { x: 0.5, y: 0.4 } as const;

/** The picture of one header, read from the kit asset params. */
export interface SpotifyHeaderParams {
  source: SpotifyHeaderSource;
  /** `photo`: the file of the account. */
  fileId: number | null;
  /** `bank`: the cover bank picture; `ai`: the AI picture of the song. */
  pictureUuid: string | null;
  focusX: number;
  focusY: number;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function focusOf(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? clamp01(value) : fallback;
}

/**
 * The picture of a header from its params (`headerSource`, `headerFileId`, `headerPictureUuid`,
 * `focusX`, `focusY`). Absent or unknown source: the blurred cover. A source with no id it needs
 * falls back to the blurred cover too. Pure.
 */
export function spotifyHeaderParamsOf(params: unknown): SpotifyHeaderParams {
  const record =
    params !== null && typeof params === 'object' && !Array.isArray(params) ? (params as Record<string, unknown>) : {};
  const wanted = isSpotifyHeaderSource(record.headerSource) ? record.headerSource : SPOTIFY_HEADER_DEFAULT_SOURCE;
  const fileId =
    typeof record.headerFileId === 'number' && Number.isInteger(record.headerFileId) && record.headerFileId > 0
      ? record.headerFileId
      : null;
  const pictureUuid =
    typeof record.headerPictureUuid === 'string' && record.headerPictureUuid.length > 0
      ? record.headerPictureUuid
      : null;
  const needs = spotifyHeaderSourceNeeds(wanted);
  const complete = needs === null || (needs === 'file' ? fileId !== null : pictureUuid !== null);
  const source = complete ? wanted : SPOTIFY_HEADER_DEFAULT_SOURCE;
  const moves = spotifyHeaderSourceMoves(source);
  return {
    source,
    fileId: source === 'photo' ? fileId : null,
    pictureUuid: source === 'bank' || source === 'ai' ? pictureUuid : null,
    focusX: moves ? focusOf(record.focusX, SPOTIFY_HEADER_DEFAULT_FOCUS.x) : SPOTIFY_HEADER_DEFAULT_FOCUS.x,
    focusY: moves ? focusOf(record.focusY, SPOTIFY_HEADER_DEFAULT_FOCUS.y) : SPOTIFY_HEADER_DEFAULT_FOCUS.y,
  };
}

/** The params that a make request stores for a header: only the keys of the picked source. Pure. */
export function spotifyHeaderStoredParams(header: SpotifyHeaderParams): Record<string, string | number> {
  const stored: Record<string, string | number> = { headerSource: header.source };
  if (header.fileId !== null) {
    stored.headerFileId = header.fileId;
  }
  if (header.pictureUuid !== null) {
    stored.headerPictureUuid = header.pictureUuid;
  }
  if (spotifyHeaderSourceMoves(header.source)) {
    stored.focusX = header.focusX;
    stored.focusY = header.focusY;
  }
  return stored;
}

/** The place of a scaled picture in the frame: scale it to `width` x `height`, then cut the frame at `left`, `top`. */
export interface HeaderCrop {
  width: number;
  height: number;
  left: number;
  top: number;
}

/**
 * The crop of a picture into a frame: the picture covers the frame (no empty edge), and its focus
 * point goes as near as it can to the middle of the subject box (the header box by default; the
 * artist Profile passes the box of its file, `artistProfile.ts`). Whole pixels. Pure.
 */
export function spotifyHeaderCrop(
  picture: { width: number; height: number },
  frame: { width: number; height: number },
  focus: { x: number; y: number },
  subject: HeaderBox = SPOTIFY_HEADER_ZONES.subject
): HeaderCrop {
  const scale = Math.max(frame.width / Math.max(1, picture.width), frame.height / Math.max(1, picture.height));
  const width = Math.max(frame.width, Math.round(picture.width * scale));
  const height = Math.max(frame.height, Math.round(picture.height * scale));
  const targetX = ((subject.x0 + subject.x1) / 2) * frame.width;
  const targetY = ((subject.y0 + subject.y1) / 2) * frame.height;
  const left = Math.round(clamp(clamp01(focus.x) * width - targetX, 0, width - frame.width));
  const top = Math.round(clamp(clamp01(focus.y) * height - targetY, 0, height - frame.height));
  return { width, height, left, top };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * The blurred cover (design a1): a small copy of the cover blurred with this sigma, zoomed so the
 * soft edge stays out of the frame, with more saturation. The web preview uses a CSS blur of the
 * same share of the frame width (`spotifyHeaderCssBlurPx`).
 */
export const SPOTIFY_HEADER_COVER_BLUR = { copyPx: 480, sigma: 14, zoom: 1.3, saturation: 1.25 } as const;

/** The CSS blur radius of the blurred cover in a preview `frameWidth` px wide. Pure. */
export function spotifyHeaderCssBlurPx(frameWidth: number): number {
  const { copyPx, sigma, zoom } = SPOTIFY_HEADER_COVER_BLUR;
  // A CSS blur is about half as strong as a Gaussian of the same radius (`CSS_BLUR_MATCH` of the renderer).
  return Math.round(((2 * sigma * frameWidth * zoom) / copyPx) * 10) / 10;
}

function svgFrame(frame: { width: number; height: number }, body: string): string {
  const { width, height } = frame;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${body}</svg>`;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

function hexOr(value: string, fallback: string): string {
  return HEX.test(value) ? value : fallback;
}

/**
 * The wash over the blurred cover: the theme background from 20 % at the top to 45 % at the foot,
 * so the name that Spotify draws bottom left stays readable. An SVG of the frame size. Pure.
 */
export function spotifyHeaderWashSvg(
  colours: Pick<ThemeColours, 'background'>,
  frame: { width: number; height: number }
): string {
  const ground = hexOr(colours.background, '#000000');
  return svgFrame(
    frame,
    `<defs><linearGradient id="w" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${ground}" stop-opacity="0.2"/><stop offset="1" stop-color="${ground}" stop-opacity="0.45"/></linearGradient></defs><rect width="100%" height="100%" fill="url(#w)"/>`
  );
}

/**
 * The theme field (design a1, "Theme colours"): the surface to the background on a diagonal, a
 * soft glow of the accent in the subject box and a weaker glow of the text colour bottom left. An
 * SVG of the frame size. Pure.
 */
export function spotifyHeaderThemeSvg(
  colours: Pick<ThemeColours, 'background' | 'surface' | 'accent' | 'text'>,
  frame: { width: number; height: number }
): string {
  const background = hexOr(colours.background, '#120a24');
  const surface = hexOr(colours.surface, background);
  const accent = hexOr(colours.accent, '#6417fb');
  const text = hexOr(colours.text, '#ffffff');
  return svgFrame(
    frame,
    `<defs>` +
      `<linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${surface}"/><stop offset="1" stop-color="${background}"/></linearGradient>` +
      `<radialGradient id="a" cx="0.62" cy="0.38" r="0.6" gradientTransform="translate(0.62 0.38) scale(0.43 1) translate(-0.62 -0.38)"><stop offset="0" stop-color="${accent}" stop-opacity="0.33"/><stop offset="0.7" stop-color="${accent}" stop-opacity="0"/></radialGradient>` +
      `<radialGradient id="t" cx="0.2" cy="0.9" r="0.5" gradientTransform="translate(0.2 0.9) scale(0.43 1) translate(-0.2 -0.9)"><stop offset="0" stop-color="${text}" stop-opacity="0.19"/><stop offset="0.7" stop-color="${text}" stop-opacity="0"/></radialGradient>` +
      `</defs>` +
      `<rect width="100%" height="100%" fill="url(#g)"/><rect width="100%" height="100%" fill="url(#a)"/><rect width="100%" height="100%" fill="url(#t)"/>`
  );
}
